import { ref } from 'vue';
import store from '@/store';
import { config } from '@/config.js';
import { t } from '@/utils/i18n-helper';

/**
 * Composable para el polling de verificación 3DS tras un challenge iframe.
 *
 * @param {Object} options
 * @param {import('vue').Ref<string>} options.iframeUrl — ref controlada por el padre
 * @param {import('vue').Ref<boolean>} options.procesandoPago — ref controlada por el padre
 * @param {import('vue').Ref<string>} options.errorMsg — ref controlada por el padre
 * @param {() => void} options.onSuccess — callback cuando el pago se completa
 * @param {() => void} options.onResetForm — callback para resetear el formulario
 * @param {() => Array<{id: number}>} options.getPlazos — función que devuelve los plazos actuales
 * @param {() => string} options.getTipoVenta — función que devuelve el tipo de venta
 * @param {Function} [options.emit] — emit del componente padre para 'pago-completado'
 */
export function use3DSPolling({ iframeUrl, procesandoPago, errorMsg, onSuccess, onResetForm, getPlazos, getTipoVenta, emit }) {
    const threeDSPollTimer = ref(null);

    const stop3DSPolling = () => {
        if (threeDSPollTimer.value) {
            clearInterval(threeDSPollTimer.value);
            threeDSPollTimer.value = null;
        }
    };

    const start3DSPolling = (pollTid, token) => {
        stop3DSPolling();
        let pollAttempts = 0;
        const MAX_POLL_ATTEMPTS = 20; // ~60 seconds at 3s intervals
        threeDSPollTimer.value = setInterval(async() => {
            try {
                pollAttempts++;
                if (pollAttempts >= MAX_POLL_ATTEMPTS) {
                    stop3DSPolling();
                    iframeUrl.value = '';
                    procesandoPago.value = false;
                    errorMsg.value = t('perfil.error_pago_plazos_timeout', {}, 'El pago está tardando demasiado. Inténtalo de nuevo.');
                    return;
                }
                const res = await store.dispatch(
                    config.getVuexAlias('front.perfil.procesar-pago-plazos'),
                    {
                        filters: {},
                        jetToken: token,
                        tid: pollTid,
                        plazos: getPlazos().map(p => p.id),
                        guardarTarjeta: false,
                        tipo_venta: getTipoVenta() || 'plazosAbono',
                    }
                );
                const response = res.data?.response || res.data;
                if (response.compraFinalizada || (!res.data?.error && !response.challengeUrl)) {
                    stop3DSPolling();
                    iframeUrl.value = '';
                    procesandoPago.value = false;
                    emit?.('pago-completado');
                    onSuccess?.();
                    onResetForm?.();
                } else if (res.data?.error && !response.challengeUrl) {
                    stop3DSPolling();
                    iframeUrl.value = '';
                    procesandoPago.value = false;
                    errorMsg.value = res.data?.message || t('perfil.error_pago_plazos', {}, 'Error al procesar el pago');
                }
            } catch (e) {
                stop3DSPolling();
                iframeUrl.value = '';
                procesandoPago.value = false;
                errorMsg.value = e?.response?.data?.message || t('perfil.error_pago_plazos', {}, 'Error al procesar el pago');
            }
        }, 3000);
    };

    return {
        start3DSPolling,
        stop3DSPolling,
        threeDSPollTimer,
    };
}
